import { apiClient } from "../apiClient";

// --- Types ---

export interface Organization {
  id: string;
  name: string;
  legacy_whitelabel_id: number | null;
  fleet_count: number;
  payment_config_count: number;
  created_at: string;
  updated_at: string;
}

export interface Fleet {
  id: string;
  organization_id: string;
  name: string;
  city_ids: number[];
  payment_configuration_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentConfiguration {
  id: string;
  organization_id: string;
  currency: string;
  payment_providers: PaymentProviders | null;
  min_direct_charge_minor?: number | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentProviders {
  stripe?: StripeProviderConfig | null;
}

export interface StripeProviderConfig {
  publishable_key: string;
}

// --- DTOs ---

export interface CreateOrganizationDto {
  name: string;
  legacy_whitelabel_id?: number | null;
}

export interface UpdateOrganizationDto {
  name?: string;
}

export interface CreateFleetDto {
  name: string;
  city_ids?: number[];
  payment_configuration_id?: string | null;
}

export interface UpdateFleetDto {
  name?: string;
  city_ids?: number[];
  payment_configuration_id?: string | null;
}

export interface CreatePaymentConfigurationDto {
  currency: string;
  payment_providers?: PaymentProviders | null;
  min_direct_charge_minor?: number | null;
}

export interface UpdatePaymentConfigurationDto {
  currency?: string;
  payment_providers?: PaymentProviders | null;
  min_direct_charge_minor?: number | null;
}

// --- API Functions ---

export async function getOrganizations(): Promise<Organization[]> {
  const response = await apiClient.get("/backend/api/v1/admin/organizations");
  return response.data?.body?.organizations ?? response.data;
}

export async function createOrganization(
  dto: CreateOrganizationDto
): Promise<Organization> {
  const response = await apiClient.post(
    "/backend/api/v1/admin/organizations",
    dto
  );
  return response.data;
}

export async function updateOrganization(
  id: string,
  dto: UpdateOrganizationDto
): Promise<Organization> {
  const response = await apiClient.put(
    `/backend/api/v1/admin/organizations/${id}`,
    dto
  );
  return response.data;
}

export async function getFleetsByOrganization(
  organizationId: string
): Promise<Fleet[]> {
  const response = await apiClient.get(
    `/backend/api/v1/admin/organizations/${organizationId}/fleets`
  );
  return response.data?.body?.fleets ?? [];
}

export async function createFleet(
  organizationId: string,
  dto: CreateFleetDto
): Promise<Fleet> {
  const response = await apiClient.post(
    `/backend/api/v1/admin/organizations/${organizationId}/fleets`,
    dto
  );
  return response.data;
}

export async function updateFleet(
  id: string,
  dto: UpdateFleetDto
): Promise<Fleet> {
  const response = await apiClient.put(
    `/backend/api/v1/admin/fleets/${id}`,
    dto
  );
  return response.data;
}

export async function getPaymentConfigsByOrganization(
  organizationId: string
): Promise<PaymentConfiguration[]> {
  const response = await apiClient.get(
    `/backend/api/v1/admin/organizations/${organizationId}/payment-configurations`
  );
  return response.data?.body?.payment_configurations ?? [];
}

export async function createPaymentConfiguration(
  organizationId: string,
  dto: CreatePaymentConfigurationDto
): Promise<PaymentConfiguration> {
  const response = await apiClient.post(
    `/backend/api/v1/admin/organizations/${organizationId}/payment-configurations`,
    dto
  );
  return response.data;
}

export async function updatePaymentConfiguration(
  id: string,
  dto: UpdatePaymentConfigurationDto
): Promise<PaymentConfiguration> {
  const response = await apiClient.put(
    `/backend/api/v1/admin/payment-configurations/${id}`,
    dto
  );
  return response.data;
}
